import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { ProductCollectionsStoreGroup, useProductCollectionsDraft } from '@/features/collections';
import { useProductStoreRows } from '@/features/store-catalog';

/** Stores shown before "Show all": each one is its own request, and the rail stays short. */
const STORES_SHOWN = 5;

/** One store's own draft, in a component of its own so the number of hooks never varies. */
function StoreCollections({
  storeId,
  storeName,
  productId,
}: {
  storeId: string;
  storeName: string;
  productId: string;
}) {
  const draft = useProductCollectionsDraft(storeId, productId);
  return <ProductCollectionsStoreGroup draft={draft} storeName={storeName} />;
}

export interface ProductCollectionsSectionProps {
  productId: string;
  /** Opens the publish modal, when the product is in no store yet. */
  onPublish: () => void;
}

/**
 * The master page's Collections card. Collections belong to a store, so it groups by store: each
 * store the product is in has its chips and an Add, and its changes go on the save bar on their own.
 */
export function ProductCollectionsSection({
  productId,
  onPublish,
}: ProductCollectionsSectionProps) {
  const { t } = useTranslation('products');
  const { rows, isPending } = useProductStoreRows(productId);
  const [showAll, setShowAll] = useState(false);
  const stores = rows.filter(({ row }) => row).map(({ store }) => store);
  // Once shown a store stays: taking it away would drop its unsaved changes.
  const shown = showAll ? stores : stores.slice(0, STORES_SHOWN);

  return (
    <SummaryCard
      title={t('masterPage.collections.title')}
      description={t('masterPage.collections.note')}
      tour="catalog.product-edit.collections"
    >
      {shown.map((store) => (
        <StoreCollections
          key={store.id}
          storeId={store.id}
          storeName={store.name}
          productId={productId}
        />
      ))}
      {shown.length < stores.length ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto justify-start self-start p-0"
          onClick={() => setShowAll(true)}
        >
          {t('masterPage.collections.showAll', { count: stores.length })}
        </Button>
      ) : null}
      {stores.length === 0 ? (
        isPending ? (
          <Skeleton className="h-5" />
        ) : (
          <div className="flex flex-col items-start gap-2">
            <p className="text-muted-foreground text-[13px]">
              {t('masterPage.collections.notInStore')}
            </p>
            {rows.length > 0 ? (
              <Can permission="products.write">
                <Button type="button" size="sm" variant="outline" onClick={onPublish}>
                  {t('masterPage.collections.publish')}
                </Button>
              </Can>
            ) : null}
          </div>
        )
      ) : null}
    </SummaryCard>
  );
}
